import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/shared/lib/cn';

export interface TooltipProps {
  /** Tresc dymka. */
  content: ReactNode;
  /** Element, ktory dymek opisuje. */
  children: ReactElement<{ 'aria-describedby'?: string }>;
  /**
   * Strona, po ktorej staje dymek. `right` centruje go w pionie wzgledem
   * elementu, `right-bottom` zaczyna go na dolnej krawedzi — uklad dla
   * zwinietego paska nawigacji, gdzie podpis ma stac pod ikona i obok niej,
   * a nie zaslaniac sasiednich pozycji.
   */
  side?: 'top' | 'bottom' | 'left' | 'right' | 'right-bottom';
  /** Klasy dymka. */
  className?: string;
  /** Klasy elementu opakowujacego opisywany element. */
  anchorClassName?: string;
}

/** Odstep dymka od opisywanego elementu. */
const TOOLTIP_GAP = 6;

/** Margines, ponizej ktorego dymek nie zblizy sie do krawedzi okna. */
const VIEWPORT_MARGIN = 8;

interface TooltipPosition {
  top: number;
  left: number;
}

/**
 * Dymek podpowiedzi.
 *
 * Pokazuje sie zarowno po najechaniu myszka, jak i po ustawieniu fokusu
 * klawiatura — bez tego druga grupa uzytkownikow nigdy nie zobaczylaby tresci.
 * Dymek jest powiazany z elementem przez `aria-describedby`, wiec czytnik ekranu
 * odczyta go razem z opisywanym elementem.
 *
 * Sam dymek zyje w portalu i stoi na `position: fixed`. Wersja rysowana
 * wewnatrz opisywanego elementu gubila sie w kazdym rodzicu z `overflow`
 * (np. w przewijanej nawigacji paska bocznego): albo byla przycieta, albo
 * rozpychala kontener i robila poziomy pasek przewijania.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  className,
  anchorClassName,
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();

  // Pozycje liczymy przed pierwszym rysowaniem, zeby dymek nie mignal w rogu.
  // `scroll` z faza przechwytywania lapie takze przewijanie wewnetrznych
  // kontenerow, nie tylko okna.
  useLayoutEffect(() => {
    if (!visible) return;

    const update = () => {
      // Mierzymy zewnetrzny `inline-flex`, a nie jego dziecko: dziecko ma
      // `display: contents`, wiec nie tworzy wlasnego boksu i jego
      // `getBoundingClientRect()` zwraca same zera — dymek ladowal wtedy
      // w rogu okna zamiast przy opisywanym elemencie.
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect || (rect.width === 0 && rect.height === 0)) return;

      const width = bubbleRef.current?.offsetWidth ?? 0;
      const height = bubbleRef.current?.offsetHeight ?? 0;

      let top: number;
      let left: number;

      switch (side) {
        case 'right':
          top = rect.top + rect.height / 2 - height / 2;
          left = rect.right + TOOLTIP_GAP;
          break;
        case 'right-bottom':
          top = rect.bottom - TOOLTIP_GAP;
          left = rect.right + TOOLTIP_GAP;
          break;
        case 'left':
          top = rect.top + rect.height / 2 - height / 2;
          left = rect.left - TOOLTIP_GAP - width;
          break;
        case 'bottom':
          top = rect.bottom + TOOLTIP_GAP;
          left = rect.left + rect.width / 2 - width / 2;
          break;
        default:
          top = rect.top - TOOLTIP_GAP - height;
          left = rect.left + rect.width / 2 - width / 2;
      }

      // Trzymamy dymek w oknie — inaczej pozycja przy krawedzi ucinalaby tresc.
      const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN;
      const maxTop = window.innerHeight - height - VIEWPORT_MARGIN;

      setPosition({
        top: Math.min(Math.max(VIEWPORT_MARGIN, top), Math.max(VIEWPORT_MARGIN, maxTop)),
        left: Math.min(Math.max(VIEWPORT_MARGIN, left), Math.max(VIEWPORT_MARGIN, maxLeft)),
      });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [visible, side, content]);

  function show() {
    setVisible(true);
  }

  function hide() {
    setVisible(false);
    setPosition(null);
  }

  return (
    <>
      <span
        ref={anchorRef}
        className={cn('inline-flex', anchorClassName)}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocusCapture={show}
        onBlurCapture={hide}
      >
        <span aria-describedby={visible ? tooltipId : undefined} className="contents">
          {children}
        </span>
      </span>

      {visible &&
        typeof document !== 'undefined' &&
        createPortal(
          <span
            ref={bubbleRef}
            id={tooltipId}
            role="tooltip"
            style={{ top: position?.top ?? 0, left: position?.left ?? 0 }}
            className={cn(
              // Dymek jest podniesiona powierzchnia w tym samym motywie, a nie
              // odwrocenie kolorow: na ciemnym motywie bialy prostokat wypalal
              // dziure w pasku bocznym.
              'bg-surface-hover text-fg-strong border-border pointer-events-none fixed z-[60] max-w-56',
              'rounded-lg border px-2.5 py-1.5 text-xs font-medium shadow-lg',
              'w-max text-center',
              // Do czasu wyliczenia pozycji dymek jest niewidoczny (bez migniecia).
              position === null && 'invisible',
              className,
            )}
          >
            {content}
          </span>,
          document.body,
        )}
    </>
  );
}
